import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';
import { useLocalConversations } from './hooks/useLocalConversations';
import lightLogo from './assets/nura-logo-light.svg';
import darkLogo from './assets/nura-logo-dark.svg';
import Sidebar from './components/Sidebar/Sidebar';
import { BsSend } from 'react-icons/bs';
// import StreamingIndicator from './components/StreamingIndicator';
// import ReactMarkdown from 'react-markdown';
import ThemeToggle from './components/ThemeToggle';
import Message from './components/Message/Message';
import LandingPage from './components/LandingPage/LandingPage';
import ChatTitle from './components/ChatTitle';

// Components


const ArtifactViewer = ({ artifact, onClose, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(artifact?.content || '');
  const iframeRef = useRef(null);

  useEffect(() => {
    if (artifact && artifact.type === 'html' && iframeRef.current) {
      const doc = iframeRef.current.contentDocument;
      doc.open();
      doc.write(artifact.content);
      doc.close();
    }
  }, [artifact]);

  const handleSave = async () => {
    try {
      const response = await fetch(`https://nura-ai-backend.vercel.app/artifacts/${artifact.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: artifact.type,
          content: editContent,
          title: artifact.title
        }),
      });

      if (response.ok) {
        const updated = await response.json();
        onUpdate(updated);
        setIsEditing(false);
      }
    } catch (error) {
      console.error('Error updating artifact:', error);
    }
  };

  if (!artifact) return null;

  const renderArtifact = () => {
    switch (artifact.type) {
      case 'html':
        return (
          <iframe
            ref={iframeRef}
            title={artifact.title}
            style={{ width: '100%', height: '400px', border: '1px solid #ccc' }}
          />
        );
      case 'react':
        return (
          <div className="react-preview">
            <p>React Component Preview (would need proper setup for execution)</p>
            <pre><code>{artifact.content}</code></pre>
          </div>
        );
      default:
        return <pre><code>{artifact.content}</code></pre>;
    }
  };

  return (
    <div className="artifact-viewer">
      <div className="artifact-header">
        <h3>{artifact.title}</h3>
        <div className="artifact-controls">
          <button onClick={() => setIsEditing(!isEditing)}>
            {isEditing ? 'Cancel' : 'Edit'}
          </button>
          {isEditing && (
            <button onClick={handleSave} className="save-btn">
              Save
            </button>
          )}
          <button onClick={onClose} className="close-btn">×</button>
        </div>
      </div>

      {isEditing ? (
        <textarea
          value={editContent}
          onChange={(e) => setEditContent(e.target.value)}
          className="artifact-editor"
          rows={20}
        />
      ) : (
        <div className="artifact-content">
          {renderArtifact()}
        </div>
      )}
    </div>
  );
};

const App = () => {
  // Remove the isFirstVisit state
  // const [isFirstVisit, setIsFirstVisit] = useState(true);

  // Add a function to check if there are any conversations with messages
  const hasExistingChats = () => {
    return conversations.some(conv => conv.messages);
  };

  // Update models state to include a loading state
  const [models, setModels] = useState({});
  const [selectedModel, setSelectedModel] = useState(null);
  const [currentConversation, setCurrentConversation] = useState(null);
  // Initialize messages state without loading from localStorage
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedArtifact, setSelectedArtifact] = useState(null);
  const [temperature, setTemperature] = useState(0.7);
  const [maxTokens, setMaxTokens] = useState(null);
  const [showLanding, setShowLanding] = useState(true);  // Ensure this is true by default
  const [isStreamingEnabled, setIsStreamingEnabled] = useState(true);
  const messagesEndRef = useRef(null);

  // Replace useConversations with useLocalConversations
  const {
    conversations,
    addConversation,
    updateConversation,
    deleteConversation
  } = useLocalConversations();

  // Update fetchModels to set the first model as default
  const fetchModels = useCallback(async () => {
    try {
      const response = await fetch('https://nura-ai-backend.vercel.app/models');
      const data = await response.json();

      if (data.models && Object.keys(data.models).length > 0) {
        setModels(data.models);

        // Set the first model as default if no model is selected
        if (!selectedModel) {
          const firstModelId = Object.keys(data.models)[0];
          setSelectedModel(firstModelId);
        }
      }
    } catch (error) {
      console.error('Error fetching models:', error);
      // Set a default model if API fails
      const defaultModel = {
        'gpt-3.5-turbo': {
          name: 'GPT-3.5 Turbo',
          context_window: 16385,
          max_tokens: 4096
        }
      };
      setModels(defaultModel);
      setSelectedModel('gpt-3.5-turbo');
    }
  }, [selectedModel]);

  useEffect(() => {
    fetchModels();
  }, [fetchModels]); // Add fetchModels as dependency

  useEffect(() => {
    if (messages.length > 0) {
      setShowLanding(false);
    }
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Update the createConversation function
  const createConversation = async (title = null) => {
    const defaultTitle = 'New Chat';
    const finalTitle = title || defaultTitle;

    const newConv = {
      id: `conv-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      title: finalTitle,
      messages: [],
      model: models[selectedModel]?.name,
      modelId: selectedModel,
      lastUpdated: new Date().toISOString()
    };

    addConversation(newConv);
    setMessages([]);
    setInputMessage('');
    setShowLanding(false);
    localStorage.setItem(CURRENT_CONVERSATION_KEY, newConv.id);
    setCurrentConversation(newConv);

    return newConv;
  };

  const handleDeleteConversation = (idToDelete) => {
    if (!idToDelete) return;

    // Delete from conversations
    deleteConversation(idToDelete);

    // Clear current conversation if it's the one being deleted
    if (currentConversation?.id === idToDelete) {
      setCurrentConversation(null);
      setMessages([]);
      setShowLanding(true);
    }

    // Check remaining conversations after deletion
    if (conversations.length === 1) { // Since deletion hasn't re-rendered yet
      setShowLanding(true);
    }
  };

  const CURRENT_CONVERSATION_KEY = 'nura_current_conversation';

  // Load current conversation from localStorage on mount
  useEffect(() => {
    const storedConvId = localStorage.getItem(CURRENT_CONVERSATION_KEY);
    if (storedConvId && conversations.length > 0) {
      const conv = conversations.find(c => c.id === storedConvId);
      if (conv) {
        setCurrentConversation(conv);
        setMessages(conv.messages || []);
        setShowLanding(conv.messages?.length === 0);
      } else {
        localStorage.removeItem(CURRENT_CONVERSATION_KEY);
        setShowLanding(true);
        setMessages([]);
        setCurrentConversation(null);
      }
    }
  }, [conversations]);

  // Update current conversation in localStorage when it changes
  useEffect(() => {
    if (currentConversation?.id) {
      localStorage.setItem(CURRENT_CONVERSATION_KEY, currentConversation.id);
    } else {
      localStorage.removeItem(CURRENT_CONVERSATION_KEY);
    }
  }, [currentConversation]);

  const selectConversation = useCallback((conversation) => {
    if (currentConversation?.id !== conversation.id) {
      setCurrentConversation(conversation);
      setMessages(conversation.messages || []);

      // Set the correct model ID from the conversation
      if (conversation.modelId && models[conversation.modelId]) {
        setSelectedModel(conversation.modelId);
      } else {
        // Fallback to finding model by name
        const modelEntry = Object.entries(models).find(([_, m]) => m.name === conversation.model);
        if (modelEntry) {
          setSelectedModel(modelEntry[0]);
        }
      }
      setShowLanding(false);
    }
  }, [currentConversation, models]);

  const handleSend = async () => {
    if (!inputMessage.trim() || isLoading) return;

    try {
      setIsLoading(true);

      const userMessage = {
        role: 'user',
        content: inputMessage.trim(),
        timestamp: new Date().toISOString(),
      };

      // Create new conversation if none exists
      if (!currentConversation) {
        const title = generateTitleFromMessage(userMessage);
        const model = models[selectedModel];
        const newConv = await createConversation(title);
        if (!newConv) return;

        // Update the conversation with correct model info
        const updatedConv = {
          ...newConv,
          title: title,
          model: model.name,
          modelId: selectedModel,
          messages: [userMessage],
          lastUpdated: new Date().toISOString()
        };
        updateConversation(newConv.id, updatedConv);
        setCurrentConversation(updatedConv);
      }

      // Create updated messages array with the new user message
      const updatedMessages = [...messages, userMessage];
      setMessages(updatedMessages);
      setInputMessage('');

      const assistantMessage = {
        role: 'assistant',
        content: '',
        model: selectedModel, // Add the model information
        timestamp: new Date().toISOString()
      };

      // Create messagesWithAssistant before using it
      const messagesWithAssistant = [...updatedMessages, assistantMessage];

      const response = await fetch('https://nura-ai-backend.vercel.app/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: selectedModel,
          messages: updatedMessages,
          temperature: temperature,
          stream: isStreamingEnabled,
          ...(maxTokens && { max_tokens: maxTokens })
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          `HTTP error! status: ${response.status}${errorData ? ` - ${errorData.error || errorData.message}` : ''
          }`
        );
      }

      if (isStreamingEnabled) {
        // Handle streaming response
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let fullContent = '';

        try {
          while (true) {
            const { value, done } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value);
            const lines = chunk.split('\n').filter(line => line.trim() !== '');

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                const data = line.slice(5).trim();

                // Skip if it's the end marker
                if (data === '[DONE]') continue;

                try {
                  const parsed = JSON.parse(data);
                  if (parsed.choices?.[0]?.delta?.content) {
                    fullContent += parsed.choices[0].delta.content;
                    // Update messages with streaming content
                    updateMessagesWithContent(messagesWithAssistant, fullContent);
                  }
                } catch (parseError) {
                  console.error('Error parsing chunk:', data, parseError);
                  continue;
                }
              }
            }
          }
        } catch (streamError) {
          console.error('Stream reading error:', streamError);
          throw streamError;
        }

        // Final update after stream ends
        if (fullContent) {
          updateMessagesWithContent(messagesWithAssistant, fullContent);
        }
      } else {
        // Handle non-streaming response
        const data = await response.json();
        const content = data.choices?.[0]?.message?.content || data.response || '';
        updateMessagesWithContent(messagesWithAssistant, content);
      }

    } catch (error) {
      console.error('Error in chat:', error);
      // Add user-friendly error message to chat
      const errorMessage = {
        role: 'assistant',
        content: `Error: Unable to get response from the server. ${error.message}`,
        timestamp: new Date().toISOString(),
      };
      setMessages([...messages, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const createArtifact = async (language, content) => {
    try {
      const response = await fetch('https://nura-ai-backend.vercel.app/artifacts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: language,
          content: content,
          title: `New ${language} artifact`
        }),
      });

      if (response.ok) {
        const newArtifact = await response.json();
        setSelectedArtifact(newArtifact);
      }
    } catch (error) {
      console.error('Error creating artifact:', error);
    }
  };

  const updateArtifact = async (updatedArtifact) => {
    try {
      setSelectedArtifact(updatedArtifact);
    } catch (error) {
      console.error('Error updating artifact:', error);
    }
  };

  // Add this helper function before handleSend
  const updateMessagesWithContent = (messages, content) => {
    const updatedMessages = messages.map((msg, index) => {
      if (index === messages.length - 1 && msg.role === 'assistant') {
        return {
          ...msg,
          content,
          model: selectedModel // Ensure model is included in updates
        };
      }
      return msg;
    });
    setMessages(updatedMessages);

    // Update conversation in storage
    if (currentConversation) {
      const updatedConversation = {
        ...currentConversation,
        messages: updatedMessages,
        lastUpdated: new Date().toISOString()
      };
      updateConversation(currentConversation.id, updatedConversation);
    }
  };

  // Update the chat header to always show model info
  const renderModelInfo = () => {
    const model = models[selectedModel];
    if (!model) return null;

    return (
      <div className="chat-header">
        <div className="header-content">
          <ChatTitle
            title={currentConversation?.title || 'New Chat'}
            onUpdate={handleTitleUpdate}
          />
          <div className="model-info">
            <span>
              {model.name} |
              Context: {model.context_window.toLocaleString()} tokens |
              Max Output: {model.max_tokens.toLocaleString()} tokens
            </span>
          </div>
          <ThemeToggle />
        </div>
      </div>
    );
  };

  // Add this function before renderModelInfo
  const handleTitleUpdate = (newTitle) => {
    if (!currentConversation) return;

    const updatedConversation = {
      ...currentConversation,
      title: newTitle,
      lastUpdated: new Date().toISOString()
    };

    // Update in storage and state
    updateConversation(currentConversation.id, updatedConversation);
    setCurrentConversation(updatedConversation);
  };

  // Update localStorage when messages change
  useEffect(() => {
    if (currentConversation && messages.length > 0) {
      const updatedConversation = {
        ...currentConversation,
        messages: messages,
        lastUpdated: new Date().toISOString()
      };

      // Prevent unnecessary updates if content hasn't changed
      if (JSON.stringify(currentConversation.messages) !== JSON.stringify(messages)) {
        updateConversation(currentConversation.id, updatedConversation);
      }
    }
  }, [messages, currentConversation, updateConversation]);

  // Load messages from storage on mount
  useEffect(() => {
    const currentConvId = localStorage.getItem('nura_current_conversation');
    if (currentConvId && conversations.length > 0) {
      const conv = conversations.find(c => c.id === currentConvId);
      if (conv && currentConversation?.id !== conv.id) {
        setCurrentConversation(conv);
        setMessages(conv.messages || []);
      }
    }
  }, [conversations, currentConversation]); // Added conversations dependency

  // Scroll to bottom when messages update
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const generateTitleFromMessage = (message) => {
    if (!message || !message.content) return 'New Chat';

    // Clean and format the message
    const content = message.content.trim();

    // If it starts with "what", "how", "why", etc., use the first sentence
    if (/^(what|how|why|when|where|who|which)/i.test(content)) {
      const firstSentence = content.split(/[.!?]/).shift();
      return firstSentence.length < 50 ? firstSentence : firstSentence.slice(0, 47) + '...';
    }

    // Otherwise take first 5-7 words
    const words = content.split(' ').slice(0, 7).join(' ');
    return words.length < 50 ? words : words.slice(0, 47) + '...';
  };

  // Update the return JSX to use the new renderModelInfo function
  return (
    <>
      {!hasExistingChats() ? (
        <LandingPage onNewChat={createConversation} />
      ) : (
        <div className="app">
          <div className="sidebar">
            <Sidebar
              conversations={conversations}
              onSelect={selectConversation}
              onDelete={handleDeleteConversation}
              onNew={createConversation}
              models={models}
              selectedModel={selectedModel}
              setSelectedModel={setSelectedModel}
              temperature={temperature}
              setTemperature={setTemperature}
              maxTokens={maxTokens}
              setMaxTokens={setMaxTokens}
              currentConversation={currentConversation}
              isStreamingEnabled={isStreamingEnabled}
              setIsStreamingEnabled={setIsStreamingEnabled}
            />
          </div>

          <div className="main-content">
            {showLanding && (
              <div className="landing-page">
                <div className="landing-logo">
                  <img
                    src={lightLogo}
                    alt="NuraAI"
                    className="logo-light"
                  />
                  <img
                    src={darkLogo}
                    alt="NuraAI"
                    className="logo-dark"
                  />
                </div>
              </div>
            )}
            <div className="chat-container">
              {renderModelInfo()}
              <div className="messages">
                {messages.map((message, index) => (
                  <Message
                    key={index}
                    message={message}
                    selectedModel={selectedModel}
                  />
                ))}
                {isLoading && messages[messages.length - 1]?.role !== 'assistant' && (
                  <Message
                    message={{
                      role: 'assistant',
                      content: '...',
                      timestamp: new Date().toISOString(),
                      model: models[selectedModel].name  // Add model name for loading message
                    }}
                    isStreaming={true}
                    model={models[selectedModel].name}
                  />
                )}
                <div ref={messagesEndRef} />
              </div>

              <div className="input-container">
                <div className="input-controls">
                  <textarea
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    placeholder="Type your message..."
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSend();
                      }
                    }}
                  />
                  <button
                    className="send-button"
                    onClick={handleSend}
                    disabled={!inputMessage.trim() || isLoading}
                  >
                    <BsSend />
                  </button>
                </div>
              </div>
            </div>

            {selectedArtifact && (
              <div className="artifact-panel">
                <ArtifactViewer
                  artifact={selectedArtifact}
                  onClose={() => setSelectedArtifact(null)}
                  onUpdate={updateArtifact}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default App;